<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <template v-else>
            <!-- Barra de géneros: lleva al control de ventas de ese género -->
            <GenreTabs
              :genres="genres"
              :active-slug="genreSlug"
              class="mb-6"
              @select="goToGenre"
            />

            <div class="rsv-panel">
              <div class="rsv-panel-header">
                <span class="rsv-logo">
                  <v-icon size="26" color="#ffffff">mdi-drama-masks</v-icon>
                </span>
                <span class="rsv-panel-title">RESERVAS / OBRA "{{ (play.title || '').toUpperCase() }}"</span>
              </div>

              <div class="rsv-panel-body">
                <!-- Migas, obra y acceso a la ficha (mockup "10 venta") -->
                <div class="rsv-head">
                  <div>
                    <div class="rsv-crumb">
                      CONTROL VENTAS<template v-if="genreName"> / {{ genreName }}</template>
                    </div>
                    <h3 class="rsv-play-title">{{ (play.title || '').toUpperCase() }}</h3>
                    <div class="rsv-play-sub">
                      {{ companyName }}<template v-if="availability"> / {{ availability }}</template>
                    </div>
                  </div>
                  <a class="rsv-basic" :href="editUrl">
                    DATOS BÁSICOS
                    <v-icon size="17">mdi-square-edit-outline</v-icon>
                  </a>
                </div>

                <div class="rsv-grid">
                  <!-- Columna izquierda: próximas funciones + funciones del mes -->
                  <div class="rsv-left">
                    <!-- Funciones que caen fuera del mes que se está mirando -->
                    <div v-if="upcomingReservations.length" class="rsv-cards rsv-cards-upcoming">
                      <button
                        v-for="r in upcomingReservations"
                        :key="'up-' + r.id"
                        type="button"
                        class="rsv-fn"
                        :style="fnStyle(r.info)"
                        @click="goToReservation(r)"
                      >
                        <span class="rsv-fn-title">{{ (play.title || '').toUpperCase() }}</span>
                        <span class="rsv-fn-date">{{ formatDate(r.iso) }}</span>
                      </button>
                    </div>

                    <div class="rsv-month-name">{{ monthName.toUpperCase() }} {{ year }}</div>

                    <div v-if="monthReservations.length" class="rsv-cards">
                      <button
                        v-for="r in monthReservations"
                        :key="r.id"
                        type="button"
                        class="rsv-fn"
                        :style="fnStyle(r.info)"
                        @click="goToReservation(r)"
                      >
                        <span class="rsv-fn-title">{{ (play.title || '').toUpperCase() }}</span>
                        <span class="rsv-fn-date">{{ formatDate(r.iso) }}</span>
                      </button>
                    </div>
                    <p v-else class="rsv-empty">Sin funciones en este mes.</p>

                    <button type="button" class="rsv-back" @click="goBack">
                      <v-icon size="18">mdi-undo</v-icon>
                      VOLVER
                    </button>
                  </div>

                  <!-- Columna derecha: calendario -->
                  <div class="rsv-right">
                    <div class="rsv-cal-card">
                      <div class="rsv-cal-yearbar">
                        <button type="button" class="rsv-cal-arrow" @click="changeYear(-1)">‹</button>
                        <span class="rsv-cal-year">{{ year }}</span>
                        <button type="button" class="rsv-cal-arrow" @click="changeYear(1)">›</button>
                      </div>

                      <div class="rsv-cal-months">
                        <button
                          v-for="(mo, i) in monthsShort"
                          :key="mo"
                          type="button"
                          class="rsv-cal-month"
                          :class="{ 'rsv-cal-month-active': i === month }"
                          @click="month = i"
                        >
                          {{ mo }}
                        </button>
                      </div>

                      <div class="rsv-cal-grid rsv-cal-weekhead">
                        <span v-for="w in weekdays" :key="w">{{ w }}</span>
                      </div>

                      <div
                        v-for="(week, wi) in weeks"
                        :key="wi"
                        class="rsv-cal-grid"
                      >
                        <span
                          v-for="(cell, ci) in week"
                          :key="ci"
                          class="rsv-cal-day"
                        >
                          <template v-if="cell">
                            <span
                              v-if="cell.info"
                              class="rsv-cal-mark"
                              :class="cell.info.fill ? 'rsv-cal-fill' : 'rsv-cal-outline'"
                              :style="cell.info.fill
                                ? { background: cell.info.color, borderColor: cell.info.color }
                                : { borderColor: cell.info.color, color: cell.info.color }"
                            >
                              {{ cell.day }}
                            </span>
                            <span v-else class="rsv-cal-num">{{ cell.day }}</span>
                          </template>
                        </span>
                      </div>

                      <div class="rsv-legend">
                        <div class="rsv-legend-title">ESTADOS DE RESERVAS PARA FUNCIONES</div>
                        <div class="rsv-legend-grid">
                          <div v-for="item in legend" :key="item.short" class="rsv-legend-item">
                            <span
                              class="rsv-legend-dot"
                              :class="item.fill ? '' : 'rsv-legend-dot-outline'"
                              :style="item.fill
                                ? { background: item.color, borderColor: item.color }
                                : { borderColor: item.color }"
                            ></span>
                            <span class="rsv-legend-label">{{ item.short }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchPlayAdmin, fetchMyReservations, fetchGenres } from "@/services/teatroApi";
import { statusInfo, LEGEND_ITEMS } from "./reservationStatus";
import GenreTabs from "./GenreTabs.vue";

const MONTHS_SHORT = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
const MONTHS_LONG = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const WEEKDAYS = ["DOM", "LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB"];

function pad(n) {
  return String(n).padStart(2, "0");
}

export default {
  name: "PlayReservations",
  components: { GenreTabs },
  props: {
    playId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      play: {},
      genres: [],
      reservations: [], // [{ id, iso, y, m, status, info }]
      loading: true,
      error: "",
      year: 2024,
      month: 0, // 0-11
      monthsShort: MONTHS_SHORT,
      weekdays: WEEKDAYS,
      legend: LEGEND_ITEMS,
    };
  },
  computed: {
    monthName() {
      return MONTHS_LONG[this.month] || "";
    },
    genreSlug() {
      return this.play?.genre?.slug || "";
    },
    genreName() {
      return (this.play?.genre?.name || "").toUpperCase();
    },
    companyName() {
      return (this.play?.company?.name || "").toUpperCase() || "—";
    },
    availability() {
      const start = this.formatDate(this.play.start_date);
      const end = this.formatDate(this.play.end_date);
      if (start && end) return `${start} - ${end}`;
      return start || end || "";
    },
    editUrl() {
      return `/admin-obras/${this.playId}/editar/`;
    },
    // ¿Se llegó desde el control de ventas o desde el listado de reservas?
    fromSales() {
      return window.location.pathname.startsWith("/ventas/");
    },
    // Reservas del mes/año seleccionados, ordenadas por fecha.
    monthReservations() {
      return this.reservations
        .filter((r) => r.y === this.year && r.m === this.month)
        .sort((a, b) => a.iso.localeCompare(b.iso));
    },
    // Fila superior del mockup: las 3 funciones siguientes que caen FUERA del
    // mes que se está mirando, para no perderlas de vista al navegar el año.
    upcomingReservations() {
      const current = `${this.year}-${pad(this.month + 1)}`;
      return this.reservations
        .filter((r) => r.iso.slice(0, 7) > current)
        .sort((a, b) => a.iso.localeCompare(b.iso))
        .slice(0, 3);
    },
    // Mapa iso -> reserva, para pintar los días del calendario.
    reservationsByDate() {
      const map = {};
      this.reservations.forEach((r) => {
        map[r.iso] = r;
      });
      return map;
    },
    // Semanas del mes: cada semana es un arreglo de 7 celdas (o null).
    weeks() {
      const first = new Date(this.year, this.month, 1).getDay(); // 0=Dom
      const total = new Date(this.year, this.month + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < first; i++) cells.push(null);
      for (let d = 1; d <= total; d++) {
        const iso = `${this.year}-${pad(this.month + 1)}-${pad(d)}`;
        const res = this.reservationsByDate[iso];
        cells.push({ day: d, iso, info: res ? res.info : null });
      }
      while (cells.length % 7 !== 0) cells.push(null);
      const out = [];
      for (let i = 0; i < cells.length; i += 7) out.push(cells.slice(i, i + 7));
      return out;
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const [play, reservations, genres] = await Promise.all([
          fetchPlayAdmin(this.playId),
          fetchMyReservations(),
          fetchGenres().catch(() => []),
        ]);
        this.play = play || {};
        this.genres = genres || [];

        this.reservations = (reservations || [])
          .filter((r) => String(r.play?.id) === String(this.playId))
          .map((r) => {
            const iso = r.displayDate || r.confirmedDate || r.requestedDate || null;
            if (!iso) return null;
            const [y, m] = iso.split("-").map(Number);
            return {
              id: r.id,
              iso,
              y,
              m: m - 1,
              status: r.status,
              info: statusInfo(r.status),
            };
          })
          .filter(Boolean);

        // Posicionar el calendario en la primera reserva (o en la temporada).
        this.setInitialMonth();
      } catch (e) {
        this.error = "No se pudieron cargar las reservas de la obra.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    setInitialMonth() {
      let ref = null;
      if (this.reservations.length) {
        ref = this.reservations
          .map((r) => r.iso)
          .sort((a, b) => a.localeCompare(b))[0];
      } else if (this.play.start_date) {
        ref = this.play.start_date;
      }
      if (ref) {
        const [y, m] = ref.split("-").map(Number);
        this.year = y;
        this.month = m - 1;
      } else {
        const now = new Date();
        this.year = now.getFullYear();
        this.month = now.getMonth();
      }
    },
    changeYear(delta) {
      this.year += delta;
    },
    formatDate(iso) {
      if (!iso) return "";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return `${d}/${m}/${y}`;
    },
    // Tarjeta de función: rellena o sólo con borde, según el estado.
    fnStyle(info) {
      if (!info) return {};
      if (info.fill) {
        return { background: info.color, borderColor: info.color, color: "#fff" };
      }
      return { background: "transparent", borderColor: info.color, color: info.color };
    },
    goToReservation(r) {
      window.location.href = `/reserva/${r.id}/`;
    },
    goToGenre(slug) {
      window.location.href = `/ventas/?genero=${encodeURIComponent(slug)}`;
    },
    goBack() {
      window.location.href = this.fromSales
        ? `/ventas/${this.genreSlug ? `?genero=${encodeURIComponent(this.genreSlug)}` : ""}`
        : "/reservas/obras/";
    },
  },
};
</script>

<style scoped>
.rsv-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.rsv-panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 74px;
  padding: 0 26px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
}

.rsv-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #111;
  border: 2px solid rgba(255, 255, 255, 0.5);
  flex: 0 0 auto;
}

.rsv-panel-title {
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.2px;
}

.rsv-panel-body {
  padding: 30px 34px 34px;
}

/* --- Encabezado: migas + obra + ficha ----------------------------------- */
.rsv-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 26px;
}

.rsv-crumb {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.rsv-play-sub {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  margin-top: 3px;
}

.rsv-basic {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  color: var(--color-theater-text);
  text-decoration: none;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.rsv-basic:hover {
  opacity: 0.72;
}

.rsv-grid {
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 26px;
}

/* --- Columna izquierda -------------------------------------------------- */
.rsv-play-title {
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 1px;
  margin: 0;
}

.rsv-month-name {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1px;
  margin-bottom: 14px;
}

/* --- Tarjetas de función ------------------------------------------------ */
.rsv-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.rsv-cards-upcoming {
  margin-bottom: 26px;
}

.rsv-fn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 68px;
  padding: 12px 8px;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.rsv-fn:hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
}

.rsv-fn-title {
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.6px;
  line-height: 1.2;
}

.rsv-fn-date {
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
}

.rsv-empty {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

.rsv-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 26px;
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.rsv-back:hover {
  opacity: 0.72;
}

/* --- Columna derecha: calendario --------------------------------------- */
.rsv-cal-card {
  background: #f3efe9;
  border-radius: 16px;
  padding: 18px 20px 20px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);
}

.rsv-cal-yearbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-bottom: 6px;
}

.rsv-cal-year {
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 1px;
}

.rsv-cal-arrow {
  background: transparent;
  border: none;
  color: #666;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.rsv-cal-arrow:hover {
  color: #111;
}

.rsv-cal-months {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2px 6px;
  margin-bottom: 14px;
}

.rsv-cal-month {
  background: transparent;
  border: none;
  color: #9a9a9a;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  padding: 2px 3px;
}

.rsv-cal-month:hover {
  color: #333;
}

.rsv-cal-month-active {
  color: #c8102e;
  font-weight: 800;
  text-decoration: underline;
}

.rsv-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.rsv-cal-weekhead {
  margin-bottom: 6px;
}

.rsv-cal-weekhead span {
  text-align: center;
  color: #b06a6a;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.rsv-cal-day {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
}

.rsv-cal-num {
  color: #333;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 600;
}

.rsv-cal-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid transparent;
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
}

.rsv-cal-fill {
  color: #fff;
}

.rsv-cal-outline {
  background: transparent;
}

/* --- Leyenda ------------------------------------------------------------ */
.rsv-legend {
  margin-top: 16px;
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.rsv-legend-title {
  text-align: center;
  color: #1a1a1a;
  font-family: var(--font-family-heading);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.6px;
  margin-bottom: 10px;
}

.rsv-legend-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px 10px;
}

.rsv-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
}

.rsv-legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid transparent;
  flex: 0 0 auto;
}

.rsv-legend-dot-outline {
  background: transparent !important;
}

.rsv-legend-label {
  color: #333;
  font-family: var(--font-family-heading);
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1.2;
}

@media (max-width: 860px) {
  .rsv-grid {
    grid-template-columns: 1fr;
  }

  .rsv-head {
    flex-direction: column;
  }

  .rsv-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
